import { useState } from 'react';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import type { ModelOption, ModelTag } from '@/lib/llmPresets';
import type { LiveModel } from '@/hooks/useLlmProviders';

const TAG_TEXT: Record<ModelTag, string> = { free: 'Free', light: 'Lightweight', preview: 'Preview' };
const CUSTOM = '__custom__';
const INHERIT = '__inherit__';

interface ModelPickerProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  curated: ModelOption[];
  live: LiveModel[];
  /** Overrides can fall back to the default model; the main picker cannot. */
  allowInherit?: boolean;
  placeholder?: string;
}

/** Model dropdown for the selected provider: recommended models, the provider's live list, or a custom ID. */
export function ModelPicker({ id, value, onChange, curated, live, allowInherit, placeholder = 'Select a model' }: ModelPickerProps) {
  const [custom, setCustom] = useState(false);
  const curatedIds = new Set(curated.map((m) => m.id));
  const liveOnly = live.filter((m) => !curatedIds.has(m.id));
  const known = curatedIds.has(value) || live.some((m) => m.id === value);
  const showCustomInput = custom || (!!value && !known);
  const selectValue = showCustomInput ? CUSTOM : value || (allowInherit ? INHERIT : '');

  const handle = (v: string) => {
    if (v === CUSTOM) { setCustom(true); return; }
    setCustom(false);
    onChange(v === INHERIT ? '' : v);
  };

  return (
    <div className="space-y-2">
      <Select value={selectValue} onValueChange={handle}>
        <SelectTrigger id={id}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {allowInherit && <SelectItem value={INHERIT}>Inherit default model</SelectItem>}
          {curated.length > 0 && (
            <SelectGroup>
              <SelectLabel>Recommended</SelectLabel>
              {curated.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  <span className="font-medium">{m.label}</span>
                  <span className="ms-2 text-xs text-muted-foreground">
                    {m.tags.map((t) => TAG_TEXT[t]).join(' · ')}{m.note ? ` — ${m.note}` : ''}
                  </span>
                </SelectItem>
              ))}
            </SelectGroup>
          )}
          {liveOnly.length > 0 && (
            <SelectGroup>
              <SelectLabel>Available from this provider ({liveOnly.length})</SelectLabel>
              {liveOnly.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  <span className="font-mono text-xs">{m.id}</span>
                  {m.free && <span className="ms-2 text-xs text-muted-foreground">Free</span>}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
          <SelectGroup>
            <SelectLabel>Other</SelectLabel>
            <SelectItem value={CUSTOM}>Enter a model ID…</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      {showCustomInput && (
        <Input
          aria-label="Custom model ID"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. my-org/my-model"
          className="font-mono text-sm"
        />
      )}
    </div>
  );
}
